import { css } from '@emotion/react';
import type { CSSInterpolation } from '@emotion/serialize';

import { theme, typography } from '@adflow/frontend/ui-theme';

const typoColorLookup = {
	title: theme.colors.text.primary,
	subTitle: theme.colors.text.primary,
	regular: theme.colors.text.primary,
	emptyLarge: theme.colors.text.faint,
	emptySmall: theme.colors.text.faint,
	label: theme.colors.text.secondary,
	placeholder: theme.colors.text.hint,
	inputText: theme.colors.text.primary,
	inputHelper: theme.colors.input.adornment,
	clickableLink: theme.colors.palettes.primary.main,
	count: theme.colors.text.hint,
};

const shimmerStyles = {
	loop: (color: string) => css`
		background-color: ${color};
		animation-iteration-count: infinite;
		@keyframes shimmer {
			from {
				background-position: top left;
			}
			to {
				background-position: top right;
			}
		}
	`,
	shimmerThenSolid: (color: string) => css`
		animation-iteration-count: 1;
		animation-fill-mode: 'forwards';
		@keyframes shimmer {
			0% {
				background-position: top left;
			}
			40% {
				background-position: top right;
			}
			50% {
				background: none;
				color: ${color};
			}
			100% {
				background: none;
				color: ${color};
			}
		}
	`,
};

export interface TextShimmerProps {
	typographyType: keyof typeof typoColorLookup;
	shimmerStyle?: keyof typeof shimmerStyles;
	animationSeconds?: number;
	outerStyle?: CSSInterpolation;
	textColor?: string;
	textStyleSansColor?: CSSInterpolation;
	text?: string;
	children?: string;
}

export function TextShimmer({
	typographyType,
	animationSeconds = 3,
	shimmerStyle = 'shimmerThenSolid',
	outerStyle,
	textColor,
	textStyleSansColor,
	text,
	children = '',
}: TextShimmerProps) {
	const color = textColor ?? typoColorLookup[typographyType];

	return (
		<div css={outerStyle}>
			<p
				data-testid="text-shimmer"
				css={[
					typography[typographyType],
					{
						padding: '0 100px', // used for smooth entrance / exit of shimmer effect
						margin: '0 -100px', // used to offset the additional padding ↑
						color: 'rgba(255, 255, 255, 0.001)',
						background: `-webkit-gradient(
							linear,
							left top,
							right top,
							from(${color}),
							to(${color}),
							color-stop(0.5, #fff)
						);`,
						backgroundPosition: '0 0',
						backgroundSize: '125px 100%',
						backgroundClip: 'text',
						backgroundRepeat: 'no-repeat',
						animation: `shimmer ${animationSeconds}s`,
					},
					textStyleSansColor,
					shimmerStyles[shimmerStyle](color),
				]}
			>
				{text ?? children}
			</p>
		</div>
	);
}
